import React from 'react';
import { useDrag } from 'react-dnd';
import { Box, Typography, Chip } from '@mui/material';
import { usePiers } from '../context/PiersContext';

const FlightTile = ({ flight, beltNumber, highlight }) => {
  const { beltAssignments, configuration, getPairedBelts } = usePiers();
  
  const [{ isDragging }, drag] = useDrag({
    type: 'flight',
    item: {
      flightNum: flight.flightNum,
      depTime: flight.depTime,
      fromBelt: beltNumber,
      destinations: flight.destinations || [flight.destination]
    },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  // Find all paired belts and collect flights from the group
  const pairedBelts = getPairedBelts(beltNumber);
  const beltPairFlights = [
    ...(beltAssignments[beltNumber] || []),
    ...pairedBelts.flatMap(pb => beltAssignments[pb] || [])
  ].sort((a, b) => a.depTime.localeCompare(b.depTime));

  // Check for conflicts with this flight's destinations
  const destinations = flight.destinations || [flight.destination];

  // Check if this flight is in the wrong pod based on destination routing
  const isWrongPod = (() => {
    const destGroups = configuration.destinationBeltGroups;
    if (!destGroups || !destinations.length) return false;
    const firstDest = destinations[0];
    // Find which pod this flight's destination belongs to
    let correctPod = null;
    for (const group of destGroups) {
      if (group.destinations && group.destinations.includes(firstDest)) {
        correctPod = group.beltRange;
        break;
      }
    }
    // Default pod (destinations: null) if no match
    if (!correctPod) {
      const defaultGroup = destGroups.find(g => g.destinations === null);
      if (defaultGroup) correctPod = defaultGroup.beltRange;
    }
    if (!correctPod) return false;
    // Is this belt outside the correct pod?
    return beltNumber < correctPod[0] || beltNumber > correctPod[1];
  })();
  
  // Calculate hourly density for this belt
  const getHourlyDensity = () => {
    const beltFlights = beltAssignments[beltNumber] || [];
    const [flightHour] = flight.depTime.split(':').map(Number);
    
    // Count flights in the same hour on this belt
    const flightsInSameHour = beltFlights.filter(f => {
      const [hour] = f.depTime.split(':').map(Number);
      return hour === flightHour;
    }).length;
    
    return flightsInSameHour;
  };

  // Get color for departure time based on hourly density
  const getDepartureTimeColor = () => {
    const hourlyCount = getHourlyDensity();
    const maxPerHour = configuration.maxFlightsPerHour || 3;
    
    if (hourlyCount === 1) {
      return '#E2E2E2'; // Default light gray
    } else if (hourlyCount === 2) {
      return '#F9B612'; // Yellow
    } else if (hourlyCount === 3) {
      return '#FF8C00'; // Orange
    } else if (hourlyCount > maxPerHour) {
      return '#FF5252'; // Red
    }
    
    return '#E2E2E2'; // Default
  };

  const getDestinationColor = (dest) => {
    let closestConflict = null;
    
    // Look through all flights in the belt pair
    for (const otherFlight of beltPairFlights) {
      if (otherFlight.flightNum === flight.flightNum && otherFlight.depTime === flight.depTime) continue;
      
      const otherDestinations = otherFlight.destinations || [otherFlight.destination];
      if (otherDestinations.includes(dest)) {
        // Calculate time difference in minutes (wrapping midnight, to match
        // the backend's conflict detection)
        const [thisHour, thisMin] = flight.depTime.split(':').map(Number);
        const [otherHour, otherMin] = otherFlight.depTime.split(':').map(Number);
        const thisMinutes = thisHour * 60 + thisMin;
        const otherMinutes = otherHour * 60 + otherMin;
        const rawDiff = Math.abs(thisMinutes - otherMinutes);
        const timeDiff = Math.min(rawDiff, 1440 - rawDiff);
        
        if (!closestConflict || timeDiff < closestConflict) {
          closestConflict = timeDiff;
        }
      }
    }
    
    // Color based on closest conflict relative to configured window
    const conflictWindowMinutes = configuration.conflictWindow * 60;
    
    if (closestConflict && closestConflict < conflictWindowMinutes) {
      // Red: Less than the configured window
      return '#FF5252'; // Red
    } else if (closestConflict && closestConflict <= conflictWindowMinutes + 29) {
      // Orange: Within window + 29 minutes
      return '#FF8C00'; // Dark Orange
    } else if (closestConflict && closestConflict <= conflictWindowMinutes + 59) {
      // Yellow: Within window + 59 minutes
      return '#F9B612'; // Yellow
    }
    
    return '#E2E2E2'; // Light gray (no conflict)
  };

  return (
    <Box
      ref={drag}
      sx={{
        p: 1,
        mb: 0.5,
        bgcolor: isWrongPod ? 'rgba(255, 140, 0, 0.12)' : 'rgba(255, 255, 255, 0.05)',
        borderRadius: 1,
        border: '1px solid',
        borderColor: isWrongPod ? '#FF8C00' : highlight ? '#F9B612' : isDragging ? '#F9B612' : 'rgba(255, 255, 255, 0.1)',
        boxShadow: isWrongPod ? '0 0 6px rgba(255, 140, 0, 0.3)' : highlight ? '0 0 8px rgba(249, 182, 18, 0.4)' : 'none',
        opacity: isDragging ? 0.5 : 1,
        cursor: 'move',
        transition: 'all 0.2s',
        '&:hover': {
          borderColor: isWrongPod ? '#FF8C00' : '#F9B612',
          bgcolor: isWrongPod ? 'rgba(255, 140, 0, 0.18)' : 'rgba(249, 182, 18, 0.05)',
          transform: 'translateX(2px)',
        }
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
        <Box sx={{ minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography
              variant="body2"
              component="span"
              sx={{
                color: getDestinationColor(destinations[0]),
                fontWeight: getDestinationColor(destinations[0]) !== '#E2E2E2' ? 700 : 600,
                fontSize: '0.875rem'
              }}
            >
              {destinations[0]}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 'bold',
                color: '#E2E2E2',
                fontSize: '0.875rem'
              }}
            >
              {flight.flightNum}
            </Typography>
            {flight.originator === 'Orig' && (
              <Chip
                label="Orig"
                size="small"
                sx={{
                  height: 16,
                  fontSize: '0.65rem',
                  bgcolor: 'rgba(249, 182, 18, 0.15)',
                  color: '#F9B612',
                  border: '1px solid rgba(249, 182, 18, 0.4)',
                  fontWeight: 600,
                  '& .MuiChip-label': {
                    px: 0.5
                  }
                }}
              />
            )}
            {(flight.aircraft === '-800' || flight.aircraft === 'MAX8') && (
              <Chip
                label={flight.aircraft}
                size="small"
                sx={{
                  height: 16,
                  fontSize: '0.65rem',
                  bgcolor: 'rgba(0, 0, 0, 0.2)',
                  color: '#E2E2E2',
                  border: '1px solid rgba(255, 255, 255, 0.3)',
                  fontWeight: 500,
                  '& .MuiChip-label': {
                    px: 0.5
                  }
                }}
              />
            )}
          </Box>
          {destinations.length > 1 && (
            <Typography
              variant="body2"
              component="div"
              sx={{ display: 'flex', gap: 0.5, alignItems: 'center', mt: 0.25, fontSize: '0.75rem' }}
            >
              {destinations.slice(1).map((dest, index) => (
                <React.Fragment key={index}>
                  {index > 0 && (
                    <span style={{ color: '#666', fontSize: '0.65rem', fontWeight: 400 }}>›</span>
                  )}
                  <span
                    style={{
                      color: getDestinationColor(dest),
                      fontWeight: getDestinationColor(dest) !== '#E2E2E2' ? 700 : 400
                    }}
                  >
                    {dest}
                  </span>
                </React.Fragment>
              ))}
            </Typography>
          )}
        </Box>
        <Typography
          variant="caption"
          sx={{
            color: getDepartureTimeColor(),
            fontWeight: 600,
            fontSize: '0.75rem',
            flexShrink: 0
          }}
        >
          {flight.depTime}
        </Typography>
      </Box>
    </Box>
  );
};

export default FlightTile;